import { Button } from "@/components/ui/button";
import { Bell, BellOff, BellRing, ShieldAlert } from "lucide-react";
import useWebPush from "@/hooks/useWebPush";

/**
 * Drop-in widget for the public tracking page. Lets buyers opt in to
 * OS-level Web Push notifications for status updates on this package.
 */
export default function PushNotifyToggle({ qrCodeId }) {
  const { state, busy, enable, disable, vapidEnabled } = useWebPush(qrCodeId);

  if (state === "unsupported" || !vapidEnabled) return null;

  if (state === "loading") {
    return (
      <div data-testid="push-toggle-loading" className="text-xs font-mono-tech text-slate-500">
        Checking notification support…
      </div>
    );
  }

  if (state === "blocked") {
    return (
      <section
        data-testid="push-toggle-blocked"
        className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 flex items-start gap-3"
      >
        <ShieldAlert className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-medium text-amber-200">Notifications blocked</div>
          <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
            Enable notifications for this site in your browser settings to receive package updates.
          </div>
        </div>
      </section>
    );
  }

  if (state === "subscribed") {
    return (
      <section
        data-testid="push-toggle-on"
        className="border border-green-500/30 bg-green-500/5 rounded-lg p-4 flex items-center gap-3"
      >
        <BellRing className="w-5 h-5 text-green-400 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-medium text-green-200">Push alerts on</div>
          <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
            You'll get a notification whenever this package's status changes — even when this tab is closed.
          </div>
        </div>
        <Button
          data-testid="push-toggle-off-btn"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={disable}
          className="text-slate-500 hover:text-red-400"
        >
          <BellOff className="w-4 h-4 mr-1.5" /> Off
        </Button>
      </section>
    );
  }

  // state === "off"
  return (
    <section
      data-testid="push-toggle-off"
      className="border border-slate-200 dark:border-slate-800 bg-slate-100/40 dark:bg-slate-900/40 rounded-lg p-4 flex items-center gap-3"
    >
      <Bell className="w-5 h-5 text-cyan-400 shrink-0" />
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-900 dark:text-white">Get push alerts</div>
        <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
          Be the first to know when this package ships, arrives, or is out for delivery.
        </div>
      </div>
      <Button
        data-testid="push-toggle-on-btn"
        size="sm"
        disabled={busy}
        onClick={enable}
        className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
      >
        <Bell className="w-4 h-4 mr-1.5" /> {busy ? "…" : "Enable"}
      </Button>
    </section>
  );
}
